import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { BankIdLanguage, BankIdOrderStatus, BankIdTranslation } from '../../model/bankid';
import { IsMobileDevice } from '../../../core/helper/mobile';
import { map, retry, startWith, switchMap, takeUntil, takeWhile, tap, timeout } from 'rxjs/operators';
import { BehaviorSubject, interval, Observable, Subject, Subscription, timer } from 'rxjs';
import { AccountService } from '../../service/data/account.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { UserAccountService } from '../../service/user-account.service';
import { MessageService } from '../../../core/service/message.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-bankid-auth',
    templateUrl: './bankid-auth.component.html',
    styleUrls: ['./bankid-auth.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BankIdAuthComponent implements OnInit, OnDestroy {
  @Input() isAutoStart: boolean = false;
  @Output() result = new EventEmitter<string>();
  @Output() requestFullscreen = new EventEmitter<void>();

  protected statusText: string = null;
  protected changedStatusText: string = null;
  protected statusMessage: number;
  protected qrCode: string = null;

  private isMobile: boolean;
  private orderReference: string;
  private consecutiveFetchErrors: number;
  private readonly stopBankId = new Subject<void>();

  ONE_MINUTE = 60;
  protected totalSeconds = this.ONE_MINUTE * 5; // 5 minutes in seconds
  private resetSubject = new BehaviorSubject<void>(undefined);
  protected showFailedStatusWindow: boolean = false;
  protected shouldAnnounceTime: boolean = true;
  protected isFullscreen: boolean = false;

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.code === 'Space' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.resetCountdown();
    }
  }

  protected countdown$: Observable<number> = this.resetSubject.pipe(
    switchMap(() =>
      interval(1000).pipe(
        startWith(0),
        map(i => this.totalSeconds - i),
        takeWhile(secondsLeft => secondsLeft >= 0),
        tap(secondsLeft => {
          this.checkIfShouldAnnounceTime(secondsLeft);
          if (secondsLeft === 0) {
            this.cancelBankId();
          }
        })
      )
    )
  );

  checkIfShouldAnnounceTime(seconds: number): boolean {
    // Announce at full minute marks (when seconds is divisible by 60 with no remainder)
    if (seconds >= this.ONE_MINUTE) {
      const isExactMinute = seconds % this.ONE_MINUTE === 0;
      return (this.shouldAnnounceTime = isExactMinute);
    }

    // If less than a minute remains, announce every 10 seconds
    const isMultipleOfTen = seconds % 10 === 0;
    const isPositive = seconds > 0;
    const shouldAnnounceUnderMinute = isMultipleOfTen && isPositive;

    return (this.shouldAnnounceTime = shouldAnnounceUnderMinute);
  }

  resetCountdown() {
    this.resetSubject.next();
  }

  resetCountdownAndRestartService() {
    this.statusText = null;
    this.changedStatusText = null;
    this.resetSubject.next();
    this.showFailedStatusWindow = false;
    this.startBankId(this.isAutoStart);
  }

  constructor(
    private readonly _account: AccountService,
    private readonly _messageService: MessageService,
    private readonly _userAccount: UserAccountService,
    private readonly _languageService: LanguageService,
    private readonly _windowService: NativeWindowService
  ) {}

  ngOnInit(): void {
    this.isMobile = IsMobileDevice();
    this.startBankId(this.isAutoStart);
  }

  ngOnDestroy(): void {
    this.cancelBankId();
  }

  startBankId(isAutoStart: boolean) {
    this.isAutoStart = isAutoStart;

    this.orderReference = null;
    this.qrCode = '';
    this.statusText = '';

    this._userAccount
      .bankIdAuthStart(this.isMobile, isAutoStart)
      .pipe(retry({ count: 1, delay: 1000 }), takeUntil(this.stopBankId))
      .subscribe({
        next: start => {
          this.orderReference = start.orderRef;

          this.qrCode = start.qrCode;

          this.statusText = BankIdTranslation.For(start.ui.startInstructions, this.bankIdlanguage());

          if (!this.changedStatusText && !!this.statusText && this.changedStatusText !== this.statusText) {
            this.changedStatusText = this.statusText;
          }

          this.startBankIdPolling(isAutoStart);

          if (isAutoStart) {
            this._windowService.navigate(
              this._windowService.window,
              'bankid:///?autostarttoken=' + start.autoStartToken + '&redirect=null'
            );
          }
        },
        error: () => {
          this.cancelBankId();
        },
      });
  }

  startBankIdPolling(isAutoStart: boolean): void {
    this.consecutiveFetchErrors = 0;
    this.stopBankId.next(); // cancels any in-flight polling
    this.pollBankIdStatus(isAutoStart);
  }

  private pollBankIdStatus(isAutoStart: boolean): void {
    if (!this.orderReference) return;

    this._userAccount
      .bankIdAuthStatus(this.isMobile, isAutoStart, this.orderReference)
      .pipe(
        timeout(10000), // allow enough time if backend is slow
        takeUntil(this.stopBankId)
      )
      .subscribe({
        next: status => {
          if (status.orderRef !== this.orderReference) {
            this.cancelBankId();
            return;
          }
          try {
            this.consecutiveFetchErrors = 0;
            this.statusMessage = status.statusMessage;
            this.qrCode = status.qrCode;
            this.statusText = BankIdTranslation.For(status.statusTranslation, this.bankIdlanguage());

            if (status.status !== BankIdOrderStatus.Pending) {
              this.stopBankId.next();

              if (status.status === BankIdOrderStatus.Complete) {
                let result = this.orderReference;
                this.orderReference = null;
                this.qrCode = '';
                this.result.emit(result);
              } else if (!this.isAutoStart && status.status === BankIdOrderStatus.Failed && status.statusMessage === -105) {
                this.startBankId(this.isAutoStart);
              } else {
                this.cancelBankId();
              }
            } else {
              // Schedule the next poll only after the previous request has completed
              setTimeout(() => this.pollBankIdStatus(isAutoStart), 1000);
            }
          } catch (error) {
            this._messageService.showError(error);
            this.cancelBankId(true);
          }
        },
        error: () => {
          this.consecutiveFetchErrors += 1;
          if (this.consecutiveFetchErrors >= 10) {
            this.cancelBankId();
          } else {
            setTimeout(() => this.pollBankIdStatus(isAutoStart), 1000);
          }
        },
      });
  }

  cancelBankId(unknownError?: boolean) {
    this.stopBankId.next();
    this.showFailedStatusWindow = true;
    this.statusMessage = null;

    console.log(unknownError);
    if (!this.isAutoStart) {
      this.changedStatusText = this._languageService.instant('LOGIN.ERROR_BANKID_TIMEOUT');
    } else if (unknownError) {
      this.changedStatusText = this._languageService.instant('LOGIN_BANKID.ERROR_BANKID_ONDEVICE');
    } else {
      this.changedStatusText = this.statusText;
    }
    if (this.orderReference) {
      this._userAccount.bankIdCancel(this.orderReference).subscribe();
      this.orderReference = null;
      this.qrCode = '';
    }
  }

  public bankIdlanguage(): BankIdLanguage {
    const currentLang = this._languageService.currentLanguage;
    switch (currentLang) {
      case 'en':
        return BankIdLanguage.English;
      case 'sv':
        return BankIdLanguage.Swedish;
      default:
        return 0 as BankIdLanguage;
    }
  }

  toggleFullscreen() {
    this.requestFullscreen.emit();
  }
}
